import ContentHeader from '../components/ContentHeader';
import { useCallback, useEffect, useState, useMemo } from 'react';
import SvgViewer from '../components/SvgViewer';
import { isCartOnlyFullAuto } from '../helpers/stepNavigation.js';
import Footer from '../components/Footer';
import MobileBottomBar from '../components/MobileBottomBar';
import Chip from '../components/Chip';
import {
    getAutoSeating,
    useGetFreeTicketSeats,
    useGetTicketSeats,
} from '../../openapi/ticket-seats/ticket-seats';
import { useGetArrangementPositions } from '../../openapi/shows/shows';
import { applyArrangementOverlay, buildPositionMap } from '../helpers/arrangementOverlay';
import Steps from '../components/Steps';
import NextToEvent from '../components/NextToEvent';
import { ICartItem, useCart } from '../hooks/useCart';
import { getNormalizedShowId } from '../helpers/show';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types';
import { calculateColorMap } from '../helpers/colorMap';
import {
    ShowSuccessResponseResponse,
    TicketSeatsSuccessResponseResponseTicketSeatsItem,
    TicketTypesSuccessResponseResponseTicketTypesItem,
    GetAutoSeating200SuggestionsItem,
} from '../../openapi/model';
import { II18nLanguageObj, useLang } from '../hooks/useLanguage';
import { useMarketing } from '../hooks/useMarketing';
import { buildGa4ItemsFromCart, sumGa4ItemsValue } from '../helpers/ga4Items';

interface iElement {
    id: number;
    name: string;
    nameI18n: II18nLanguageObj;
    x: number;
    y: number;
    rotation: number;
    scale: number;
    svg: string;
    bookable: boolean;
}

function mapFromDbToElement(seat: TicketSeatsSuccessResponseResponseTicketSeatsItem): iElement {
    let svgInfo = seat?.svgInfo;
    if (svgInfo === null || svgInfo === undefined) {
        svgInfo = {};
    }
    svgInfo.x = svgInfo.x || 0;
    svgInfo.y = svgInfo.y || 0;
    svgInfo.rotation = svgInfo.rotation || 0;
    svgInfo.scale = svgInfo.scale || 1;
    svgInfo.svg = svgInfo.svg || '';
    svgInfo.model = svgInfo.model || '';

    return {
        // keep the original data
        id: seat.id,
        name: seat.name,
        nameI18n: seat.nameI18n,
        // map svg data from the svg section
        x: svgInfo.x,
        y: svgInfo.y,
        rotation: svgInfo.rotation,
        scale: svgInfo.scale,
        model: svgInfo.model,
        svg: svgInfo.svg,
        bookable: seat.bookable,
    };
}

function getSeatNeeds(
    cart: ICartItem[],
    ticketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[] | undefined,
) {
    if (!ticketTypes) {
        return {};
    }

    // group by ticketTypeId
    const grouped: { [key: string]: number } = {};
    cart.forEach((ticket) => {
        const ticketType = ticketTypes.find((type) => type.id === ticket.ticketTypeId);
        if (!ticketType || ticketType.seats?.length === 0) {
            return;
        }
        if (!grouped[ticket.ticketTypeId]) {
            grouped[ticket.ticketTypeId] = 0;
        }
        grouped[ticket.ticketTypeId] += ticket.quantity;
    });
    return grouped;
}

const CURRENT_STEP = 3;

function Step3({
    setStep,
    show,
    setAutoSeatingSkipStep3,
    delayedSeatAssignmentEnabled = false,
}: {
    setStep: (step: number) => void;
    show: ShowSuccessResponseResponse;
    setAutoSeatingSkipStep3: (skip: boolean) => void;
    delayedSeatAssignmentEnabled?: boolean;
}) {
    const [elements, setElements] = useState<iElement[]>([]);
    const { cart, timeslot, seats, setSeats } = useCart();
    const { beginCheckout } = useMarketing();
    const normalizedShowId = getNormalizedShowId(show);
    const [suggestedSeats, setSuggestedSeats] = useState<GetAutoSeating200SuggestionsItem[]>([]);
    const [isErrorSuggestions, setIsErrorSuggestions] = useState(false);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [showLabel, setShowLabel] = useState(false);
    const { data: { ticketSeats: allSeats } = { ticketSeats: [] } } = useGetTicketSeats(
        show?.id || 0,
        { pageSize: 1000, page: 0 },
    );
    const { data: { ticketTypes } = { ticketTypes: [] } } = useGetTicketTypes({
        optionalId: show?.id,
        includeSeats: true,
        onlyShowInList: true,
        onlyGuestVisible: true,
    });
    // get free ticket seats
    const { data: { ticketSeats: freeSeats } = { ticketSeats: [] } } = useGetFreeTicketSeats(
        show?.id || 0,
        {
            pageSize: 1000,
            page: 0,
            timeslotId: timeslot?.id ?? 0,
        },
    );

    // Per-timeslot override wins, falling back to the show's default arrangement.
    const activeArrangementId =
        (timeslot as { activeArrangementId?: number | null })?.activeArrangementId ??
        (show?.activeArrangementId as number | null | undefined) ??
        null;
    const { data: positionsData } = useGetArrangementPositions(
        show?.id || 0,
        activeArrangementId || 0,
        { query: { enabled: !!show?.id && !!activeArrangementId } },
    );

    const { displayElements, arrangementExcludedIds } = useMemo(() => {
        if (!activeArrangementId) {
            return { displayElements: elements, arrangementExcludedIds: new Set<number>() };
        }
        // Gate rendering until arrangement positions load so removed seats are
        // never briefly shown or clickable.
        if (!positionsData?.positions) {
            return {
                displayElements: [] as typeof elements,
                arrangementExcludedIds: new Set<number>(),
            };
        }
        const overlay = applyArrangementOverlay(
            elements,
            buildPositionMap(positionsData.positions),
        );
        return {
            displayElements: overlay.displayElements,
            arrangementExcludedIds: new Set(overlay.excludedSeatIds),
        };
    }, [elements, activeArrangementId, positionsData]);

    const filteredFreeSeats = useMemo(() => {
        if (!arrangementExcludedIds.size) return freeSeats;
        return freeSeats?.filter((s) => !arrangementExcludedIds.has(Number(s.id)));
    }, [freeSeats, arrangementExcludedIds]);

    const { getI18n, t } = useLang();

    useEffect(() => {
        if (!allSeats || allSeats.length === 0) {
            return;
        }
        setElements(allSeats.map(mapFromDbToElement));
    }, [allSeats]);

    // elements: all seats
    // ticketTypes: all ticket types

    const seatNeeds = useMemo(() => getSeatNeeds(cart, ticketTypes), [cart, ticketTypes]);
    /**
     *
     * @param {*} seatId id of the seat
     * @param {*} ticketTypeHint if non null, than the ticket type we want to assign
     * @returns
     */
    const onSeatClick = useCallback(
        (seatId: number, ticketTypeHint = null) => {
            // check if we already have this seat, if so, remove it
            const hasThisSeat = seats.find((e) => e.id === seatId);
            if (hasThisSeat) {
                // Check if this seat belongs to a fullauto ticket type - if so, prevent removal
                const ticketType = ticketTypes?.find(
                    (type) => type.id === hasThisSeat.ticketTypeId,
                );
                if (ticketType?.automaticSeating === 'fullauto') {
                    console.log('Cannot remove fullauto seat:', seatId);
                    return; // Prevent removal of fullauto seats
                }
                setSeats(seats.filter((e) => e.id !== seatId));
                return;
            }

            // new seat, lets figure out the ticketType first
            let foundTicketTypes: TicketTypesSuccessResponseResponseTicketTypesItem[] = [];
            ticketTypes.forEach((ticketType) => {
                const seatsAlreadyFromType = seats.filter((e) => e.ticketTypeId === ticketType.id);
                if (
                    ticketType.seats?.find((seat) => (seat.id as unknown as number) === seatId) &&
                    // we should only fill needs
                    seatNeeds[ticketType.id] > 0 &&
                    seatNeeds[ticketType.id] > seatsAlreadyFromType.length
                ) {
                    foundTicketTypes.push(ticketType);
                }
            });
            // if we have a ticketTypeHint, we need to check if it's in the foundTicketTypes
            if (ticketTypeHint) {
                const foundTicketType = foundTicketTypes.find(
                    (ticketType) => ticketType.id === ticketTypeHint,
                );
                foundTicketTypes = foundTicketType ? [foundTicketType] : [];
            }

            if (foundTicketTypes.length === 0) {
                console.error('No ticket type found for seat', seatId);
                return;
            }

            // add the first type we can
            const newSeat = foundTicketTypes[0].seats?.find(
                (seat) => (seat.id as unknown as number) === seatId,
            );
            if (!newSeat) {
                console.error('No seat found for seat', seatId);
                return;
            }

            // only free seats should be added to the cart
            if (
                (filteredFreeSeats?.length ?? 0) > 0 &&
                !filteredFreeSeats?.find((seat) => Number(seat.id) === seatId)
            ) {
                console.error('Seat is not free', seatId);
                return;
            }

            setSeats([
                ...seats,
                {
                    id: seatId,
                    name: newSeat.name,
                    nameI18n: newSeat.nameI18n,
                    ticketTypeId: foundTicketTypes[0].id,
                    ticketTypeName: foundTicketTypes[0].name,
                    ticketTypeNameI18n: foundTicketTypes[0].nameI18n,
                },
            ]);
        },
        [seats, setSeats, ticketTypes, seatNeeds, filteredFreeSeats],
    );

    const suggestedSeatIds = useMemo(() => {
        const ids = new Set<number>();
        suggestedSeats.forEach((suggestion) => {
            suggestion.seats?.forEach((seat) => {
                if (seat.id != null) ids.add(seat.id as number);
            });
        });
        return ids;
    }, [suggestedSeats]);

    const { colorMap, ticketTypeColorMap, ticketTypeNameI18nMap, dimmedSeatIds } =
        calculateColorMap(
            ticketTypes,
            allSeats,
            seatNeeds,
            seats,
            filteredFreeSeats,
            suggestedSeatIds,
        );

    const seatNeedNumber = Object.values(seatNeeds).reduce((a, b) => a + b, 0);

    const _onClickElement = useCallback(
        (item: number) => {
            onSeatClick(item);
        },
        [onSeatClick],
    );

    // Memoize the auto seating requirements to prevent unnecessary recalculations
    const autoSeatingRequirements = useMemo(() => {
        if (!ticketTypes || Object.keys(seatNeeds).length === 0) {
            return [];
        }

        return Object.entries(seatNeeds)
            .map(([ticketTypeId, totalQuantity]) => {
                const id = parseInt(ticketTypeId, 10);
                const ticketType = ticketTypes.find((type) => type.id === id);

                const automaticSeating = ticketType?.automaticSeating || 'manual';
                let quantity = totalQuantity;

                if (automaticSeating === 'auto' || automaticSeating === 'fullauto') {
                    const selectedCount = seats.filter((s) => s.ticketTypeId === id).length;
                    quantity = Math.max(0, totalQuantity - selectedCount);
                }

                return {
                    ticketTypeId: id,
                    quantity,
                    automaticSeating,
                };
            })
            .filter((req) => req.automaticSeating !== 'manual' && req.quantity > 0);
    }, [ticketTypes, seatNeeds, seats]);

    // Get auto seating suggestions for non-manual ticket types
    const getSeatSuggestions = useCallback(async () => {
        if (!timeslot?.id || autoSeatingRequirements.length === 0) {
            setSuggestedSeats([]);
            setIsErrorSuggestions(false);
            setIsLoadingSuggestions(false);
            return;
        }

        setIsLoadingSuggestions(true);
        try {
            const response = await getAutoSeating(timeslot.id, {
                currentSeats: seats.map((e) => {
                    return {
                        id: e.id,
                        ticketTypeId: e.ticketTypeId,
                    };
                }),
                requiredSeats: autoSeatingRequirements.map((req) => ({
                    ticketTypeId: req.ticketTypeId,
                    quantity: req.quantity,
                })),
            });

            setSuggestedSeats(response?.suggestions ?? []);
        } catch (error) {
            console.error('Error getting auto seating suggestions:', error);
            setIsErrorSuggestions(true);
        } finally {
            setIsLoadingSuggestions(false);
        }
    }, [timeslot?.id, autoSeatingRequirements, seats, activeArrangementId]);

    // Separate effect for auto-applying fullauto seats
    useEffect(() => {
        if (!suggestedSeats.length || !ticketTypes) {
            return;
        }

        const fullAutoSeats: any[] = [];
        const fullAutoTicketTypeIds: number[] = [];

        // Build the expected fullauto seats from suggestions
        suggestedSeats.forEach((suggestion) => {
            const ticketType = ticketTypes.find((type) => type.id === suggestion.ticketTypeId);
            if (ticketType?.automaticSeating === 'fullauto') {
                fullAutoTicketTypeIds.push(suggestion.ticketTypeId);
                const neededQuantity = seatNeeds[suggestion.ticketTypeId] || 0;
                suggestion.seats?.slice(0, neededQuantity).forEach((seat) => {
                    fullAutoSeats.push({
                        id: seat.id,
                        name: seat.name,
                        nameI18n: seat.nameI18n || {},
                        ticketTypeId: suggestion.ticketTypeId,
                        ticketTypeName: ticketType.name,
                        ticketTypeNameI18n: ticketType.nameI18n || {},
                    });
                });
            }
        });

        if (fullAutoSeats.length > 0) {
            // Get current fullauto seats
            const currentFullAutoSeats = seats.filter((seat) =>
                fullAutoTicketTypeIds.includes(seat.ticketTypeId),
            );

            // Create a more robust comparison to prevent loops
            const currentSeatIds = new Set(currentFullAutoSeats.map((seat) => seat.id));
            const expectedSeatIds = new Set(fullAutoSeats.map((seat) => seat.id));

            // Only update if the seat IDs are actually different
            const needsUpdate = currentSeatIds.size !== expectedSeatIds.size;
            if (needsUpdate) {
                console.log('Updating fullauto seats:', {
                    current: [...currentSeatIds],
                    expected: [...expectedSeatIds],
                });

                const nonFullAutoSeats = seats.filter(
                    (seat) => !fullAutoTicketTypeIds.includes(seat.ticketTypeId),
                );
                setSeats([...nonFullAutoSeats, ...fullAutoSeats]);
            }
        }
    }, [suggestedSeats, ticketTypes, seatNeeds, seats, setSeats]);

    useEffect(() => {
        getSeatSuggestions();
    }, [getSeatSuggestions]);

    // Clear seats when cart changes (e.g., user goes back to Step2 and modifies tickets)
    useEffect(() => {
        // If there are seats but no corresponding cart items, clear the seats
        const cartTicketTypeIds = new Set(cart.map((item) => item.ticketTypeId));
        const seatsWithoutCartItems = seats.filter(
            (seat) => !cartTicketTypeIds.has(seat.ticketTypeId),
        );

        if (seatsWithoutCartItems.length > 0) {
            console.log('Clearing orphaned seats:', seatsWithoutCartItems);
            const validSeats = seats.filter((seat) => cartTicketTypeIds.has(seat.ticketTypeId));
            setSeats(validSeats);
        }
    }, [cart, seats, setSeats]);

    // Check if there are any auto ticket types with suggestions
    const hasAutoSuggestions = useMemo(() => {
        return suggestedSeats.some((suggestion) => {
            const ticketType = ticketTypes?.find((type) => type.id === suggestion.ticketTypeId);
            return ticketType?.automaticSeating === 'auto';
        });
    }, [suggestedSeats, ticketTypes]);

    // Check if there are clearable seats (non-fullauto seats)
    const hasClearableSeats = useMemo(() => {
        return seats.some((seat) => {
            const ticketType = ticketTypes?.find((type) => type.id === seat.ticketTypeId);
            return ticketType?.automaticSeating !== 'fullauto';
        });
    }, [seats, ticketTypes]);

    // Accept all suggested seats for auto ticket types
    const acceptAllSuggestedSeats = useCallback(() => {
        type NewSeat = {
            id: number;
            name: string;
            nameI18n: II18nLanguageObj;
            ticketTypeId: number;
            ticketTypeName: string;
            ticketTypeNameI18n: II18nLanguageObj;
        };
        const newSeats: NewSeat[] = [];

        suggestedSeats.forEach((suggestion) => {
            const ticketType = ticketTypes?.find((type) => type.id === suggestion.ticketTypeId);
            if (ticketType?.automaticSeating === 'auto') {
                const currentSelectedSeats = seats.filter(
                    (seat) => seat.ticketTypeId === suggestion.ticketTypeId,
                );
                const neededQuantity =
                    suggestion.ticketTypeId !== undefined
                        ? seatNeeds[suggestion.ticketTypeId as keyof typeof seatNeeds] || 0
                        : 0;
                const remainingNeeded = neededQuantity - currentSelectedSeats.length;

                if (remainingNeeded > 0 && suggestion.seats) {
                    // filter out seats that are already selected
                    const alreadySelectedIds = new Set(currentSelectedSeats.map((s) => s.id));
                    const filteredSeats = suggestion.seats.filter(
                        (seat) => !alreadySelectedIds.has(seat.id as number),
                    );
                    filteredSeats.slice(0, remainingNeeded).forEach((seat) => {
                        newSeats.push({
                            id: seat.id as number,
                            name: seat.name as string,
                            nameI18n: seat.nameI18n || {},
                            ticketTypeId: suggestion.ticketTypeId as number,
                            ticketTypeName: ticketType.name,
                            ticketTypeNameI18n: ticketType.nameI18n || {},
                        });
                    });
                }
            }
        });

        if (newSeats.length > 0) {
            setSeats([...seats, ...newSeats]);
        }
    }, [suggestedSeats, ticketTypes, seats, seatNeeds, setSeats]);

    // Clear all seats (except fullauto seats which cannot be removed)
    const clearAllSeats = useCallback(() => {
        const fullAutoSeats = seats.filter((seat) => {
            const ticketType = ticketTypes?.find((type) => type.id === seat.ticketTypeId);
            return ticketType?.automaticSeating === 'fullauto';
        });
        setSeats(fullAutoSeats);
    }, [seats, ticketTypes, setSeats]);

    // Check if all cart items are auto or fullauto (no manual tickets)
    const allTicketsAreAutoOrFullAuto = useMemo(() => {
        if (cart.length === 0) return false;
        return cart.every((item) => {
            const ticketType = ticketTypes?.find((tt) => tt.id === item.ticketTypeId);
            return (
                ticketType?.automaticSeating === 'auto' ||
                ticketType?.automaticSeating === 'fullauto'
            );
        });
    }, [cart, ticketTypes]);

    const cartHasOnlyFullAuto = isCartOnlyFullAuto(cart, ticketTypes);

    // Skip step 3 for fullauto-only carts once seats are covered — either via
    // fresh API suggestions (first mount) or seats already assigned from a prior mount.
    useEffect(() => {
        if (!cartHasOnlyFullAuto) {
            setAutoSeatingSkipStep3(false);
            return;
        }

        const suggestionsReady = !isErrorSuggestions && suggestedSeats.length > 0;
        const seatsAlreadyCovered = autoSeatingRequirements.length === 0;

        if (suggestionsReady || seatsAlreadyCovered) {
            setAutoSeatingSkipStep3(true);
            const cartItems = buildGa4ItemsFromCart(cart, show.title);
            beginCheckout({
                items: cartItems,
                value: sumGa4ItemsValue(cartItems),
                currency: 'USD',
            });
            setStep(CURRENT_STEP + 1);
        }
    }, [
        cartHasOnlyFullAuto,
        isErrorSuggestions,
        suggestedSeats,
        autoSeatingRequirements,
        setStep,
        setAutoSeatingSkipStep3,
        beginCheckout,
        cart,
        show.title,
    ]);

    // Handler for next step that accepts auto suggestions if needed
    const handleNextStep = useCallback(() => {
        if (allTicketsAreAutoOrFullAuto && seats.length < seatNeedNumber) {
            // Accept auto suggestions before proceeding
            const newSeats: any[] = [];

            // Handle both 'auto' and 'fullauto' — the fullauto auto-apply effect may not have
            // run yet if the user clicked Next as soon as suggestions resolved.
            suggestedSeats.forEach((suggestion) => {
                const ticketType = ticketTypes?.find((type) => type.id === suggestion.ticketTypeId);
                if (
                    ticketType?.automaticSeating !== 'auto' &&
                    ticketType?.automaticSeating !== 'fullauto'
                ) {
                    return;
                }
                const currentSelectedSeats = seats.filter(
                    (seat) => seat.ticketTypeId === suggestion.ticketTypeId,
                );
                const neededQuantity = seatNeeds[suggestion.ticketTypeId] || 0;
                const remainingNeeded = neededQuantity - currentSelectedSeats.length;

                if (remainingNeeded > 0) {
                    suggestion.seats?.slice(0, remainingNeeded).forEach((seat) => {
                        newSeats.push({
                            id: seat.id,
                            name: seat.name,
                            nameI18n: seat.nameI18n || {},
                            ticketTypeId: suggestion.ticketTypeId,
                            ticketTypeName: ticketType.name,
                            ticketTypeNameI18n: ticketType.nameI18n || {},
                        });
                    });
                }
            });

            if (newSeats.length > 0) {
                setSeats([...seats, ...newSeats]);
            }
        }

        // Proceed to next step
        const cartItems = buildGa4ItemsFromCart(cart, show.title);
        beginCheckout({ items: cartItems, value: sumGa4ItemsValue(cartItems), currency: 'USD' });
        setStep(CURRENT_STEP + 1);
    }, [
        allTicketsAreAutoOrFullAuto,
        seats,
        seatNeedNumber,
        suggestedSeats,
        ticketTypes,
        seatNeeds,
        setSeats,
        setStep,
        beginCheckout,
        cart,
        show.title,
    ]);

    // Determine if Next button should be disabled
    const isNextButtonDisabled = useMemo(() => {
        if (isErrorSuggestions) return true;
        if (!allTicketsAreAutoOrFullAuto) return seatNeedNumber !== seats.length;
        if (seats.length >= seatNeedNumber) return false;
        // Wait for suggestions before allowing Next — otherwise handleNextStep sends seats=[]
        // and booking_items rows are persisted with ticket_seat_id = NULL.
        return isLoadingSuggestions || suggestedSeats.length === 0;
    }, [
        isErrorSuggestions,
        allTicketsAreAutoOrFullAuto,
        isLoadingSuggestions,
        suggestedSeats.length,
        seatNeedNumber,
        seats.length,
    ]);

    // Fullauto-only: effects fill seats and transition to step 4; no UI to show.
    // On suggestion error the skip-step3 effect won't advance, so fall through to render
    // the error message and back button rather than stranding the guest on a blank step.
    if (cartHasOnlyFullAuto && !isErrorSuggestions) return null;

    return (
        <div className="fireknife-step fireknife-step3">
            <div className="fireknife-step__main fireknife-step3__main">
                <div className="fireknife-step3__content-header">
                    <ContentHeader
                        prevStep={() => {
                            setStep(CURRENT_STEP - 1);
                        }}
                        label={t('widgetStepNavigation.step3back')}
                    />
                </div>
                <div className="fireknife-step3__select-seats">
                    <div className="fireknife-step3__select-seats-header">
                        <h2 className="fireknife-step3__select-seats-title">
                            {t('widgetInfo.selectYourSeats')}
                        </h2>
                        {isErrorSuggestions && (
                            <div className="fireknife-step3__error-message">
                                {t('widgetInfo.invalidSuggestion')}
                            </div>
                        )}
                        {/* Auto seating action buttons next to headline */}
                        {(hasAutoSuggestions || hasClearableSeats) && (
                            <div className="fireknife-step3__seat-actions">
                                {hasAutoSuggestions && (
                                    <button
                                        className="fireknife-btn fireknife-btn--xs fireknife-btn--primary"
                                        onClick={acceptAllSuggestedSeats}
                                    >
                                        {t('widgetInfo.acceptSuggestedSeats')}
                                    </button>
                                )}
                                {hasClearableSeats && (
                                    <button
                                        className="fireknife-btn fireknife-btn--xs fireknife-btn--secondary"
                                        onClick={clearAllSeats}
                                    >
                                        {t('widgetInfo.clearSeats')}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="fireknife-step3__body">
                        <div className="fireknife-step3__selected-items">
                            {Object.entries(seatNeeds).map(([ticketTypeId, quantity]) => {
                                const ticketType = ticketTypes.find(
                                    (type) => type.id === parseInt(ticketTypeId, 10),
                                );
                                if (!ticketType) {
                                    return null;
                                }
                                const hasSeats = ticketType.seats && ticketType.seats.length > 0;
                                const selectedSeats = seats.filter(
                                    (e) => e.ticketTypeId === parseInt(ticketTypeId, 10),
                                );
                                return (
                                    <div
                                        className="fireknife-select-seats__item"
                                        key={`${ticketTypeId}`}
                                    >
                                        <div className="fireknife-select-seats__block">
                                            <label className="fireknife-select-seats__label">
                                                {/* TODO:
                                                    What to to with this part? The "t('widgetInfo.selectYourSeats')" is a h2 now at the top
                                                    Do we need to display this? t('widgetInfo.noSeatingForThisTicket')
                                                hasSeats
                                                    ? t('widgetInfo.selectYourSeats')
                                                    : t('widgetInfo.noSeatingForThisTicket')
                                                */}
                                                {/* TODO: hard-coded moifier class: colors --type-1 | --type-2 | --type-3 */}
                                                <span className="fireknife-select-seats__label-icon fireknife-select-seats__label-icon--type-1"></span>
                                                <span className="fireknife-select-seats__label-text">
                                                    {getI18n(ticketType.name, ticketType.nameI18n)}
                                                </span>
                                            </label>
                                            <div className="fireknife-select-seats__form-control-box">
                                                {/* TODO: hard-coded moifier class: colors --type-1 | --type-2 | --type-3 */}
                                                <input
                                                    className="fireknife-select-seats__form-control fireknife-select-seats__form-control--type-1"
                                                    type="text"
                                                    value={quantity}
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        {hasSeats && (
                                            <div className="fireknife-select-seats__block-body">
                                                <div className="fireknife-select-seats__content-title">
                                                    {t('widgetInfo.selectedSeats')}
                                                </div>
                                                {selectedSeats.length > 0 && (
                                                    <div className="fireknife-select-seats__content-list">
                                                        {selectedSeats.map((seat) => {
                                                            const seatTicketType =
                                                                ticketTypes?.find(
                                                                    (type) =>
                                                                        type.id ===
                                                                        seat.ticketTypeId,
                                                                );
                                                            const isFullAuto =
                                                                seatTicketType?.automaticSeating ===
                                                                'fullauto';

                                                            return (
                                                                <Chip
                                                                    key={seat.id}
                                                                    label={getI18n(
                                                                        seat.name,
                                                                        seat.nameI18n,
                                                                    )}
                                                                    onRemove={
                                                                        !isFullAuto
                                                                            ? () =>
                                                                                  onSeatClick(
                                                                                      seat.id,
                                                                                  )
                                                                            : undefined
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Show suggested seats for "auto" ticket types */}
                                                {(() => {
                                                    const suggestion = suggestedSeats.find(
                                                        (s) =>
                                                            s.ticketTypeId ===
                                                            parseInt(ticketTypeId, 10),
                                                    );
                                                    const ticketType = ticketTypes.find(
                                                        (type) =>
                                                            type.id === parseInt(ticketTypeId, 10),
                                                    );

                                                    if (
                                                        !suggestion ||
                                                        !ticketType ||
                                                        ticketType.automaticSeating !== 'auto' ||
                                                        selectedSeats.length >= quantity
                                                    ) {
                                                        return null;
                                                    }

                                                    return (
                                                        <div className="fireknife-select-seats__suggestions">
                                                            <div className="fireknife-select-seats__content-title">
                                                                {t('widgetInfo.suggestedSeats')}
                                                            </div>
                                                            <div className="fireknife-select-seats__content-list">
                                                                {suggestion.seats
                                                                    ?.filter(
                                                                        (seat) =>
                                                                            !selectedSeats.some(
                                                                                (selected) =>
                                                                                    selected.id ===
                                                                                    seat.id,
                                                                            ),
                                                                    )
                                                                    .slice(
                                                                        0,
                                                                        quantity -
                                                                            selectedSeats.length,
                                                                    )
                                                                    .map((seat) => (
                                                                        <div
                                                                            className="fireknife-select-seats__content-item fireknife-select-seats__content-item--suggested"
                                                                            key={`suggested-${seat.id}`}
                                                                        >
                                                                            {getI18n(
                                                                                seat.name,
                                                                                seat.nameI18n || {},
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="fireknife-select-seat">
                            <SvgViewer
                                elements={displayElements}
                                colorMap={colorMap}
                                inCartSeatIds={seats.map((e) => e.id)}
                                onClickElement={(item: number) => {
                                    onSeatClick(item);
                                }}
                                ticketTypeColorMap={ticketTypeColorMap}
                                ticketTypeNameI18nMap={ticketTypeNameI18nMap}
                                dimmedSeatIds={dimmedSeatIds}
                                showLabel={showLabel}
                                setShowLabel={setShowLabel}
                                showSuggestedLegend={suggestedSeatIds.size > 0}
                            />
                        </div>
                    </div>
                </div>

                <Footer />
            </div>
            <aside className="fireknife-step__aside fireknife-step3__aside">
                <MobileBottomBar
                    showFees={show?.fees || []}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                    nextStep={handleNextStep}
                    label={t('widgetStepNavigation.step3next')}
                    disabled={isNextButtonDisabled}
                />
                <div className="fireknife-step__aside-content">
                    <div className="fireknife-step3__payment">
                        <Steps
                            step={3}
                            setStep={setStep}
                            showId={normalizedShowId}
                            delayedSeatAssignmentEnabled={delayedSeatAssignmentEnabled}
                            rentalMode={!!show?.marketing?.rentalMode}
                        ></Steps>
                    </div>
                </div>
                <NextToEvent
                    nextStep={handleNextStep}
                    label={t('widgetStepNavigation.step3next')}
                    disabled={isNextButtonDisabled}
                    showFees={show?.fees || []}
                    isBreakingOutFees={show.isBreakingOutFees}
                    priceListingMode={show.priceListingMode}
                    cardProcessingFee={show.cardProcessingFee}
                />
            </aside>
        </div>
    );
}

export default Step3;
